/**
 * Themed Silhouette sound palette.
 *
 * The vocabulary is editorial-quiet: paper-soft taps, breathy noise sweeps as
 * a selection draws along a curve, and a *themed chime per category* on word-
 * found events. The chime is the soul of this variant — bird trill for the
 * garden, single piano note for music, soft bell for the kitchen, ship's bell
 * for travel, needle drop for records, porcelain clink for tea time.
 *
 * Everything routes through `audio.playIfNotMuted` so the global mute
 * preference works for free.
 */
import { audio } from '@/juice';

import type { ChimeKind } from './themes';

export type ThemedSoundName =
  | 'letterTap'
  | 'letterSelect'
  | 'pathDraw'
  | 'wordTravel'
  | 'incorrectSoft'
  | 'cardEnter'
  | 'complete';

// ---------------------------------------------------------------------------
// Tiny utility — pitch jitter in cents.
// ---------------------------------------------------------------------------

function jitter(rand: () => number, cents: number): number {
  return (rand() * 2 - 1) * cents;
}

// A house RNG so the jitter doesn't read as random — same call → same jitter.
function makeLocalRand(): () => number {
  let s = (Date.now() ^ 0x9e3779b9) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const localRand = makeLocalRand();

// ---------------------------------------------------------------------------
// Letter tap — sharp pitched click. Two voices: pitched ping + filtered click.
// ---------------------------------------------------------------------------

function synthLetterTap(soft = false): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = soft ? 0.07 : 0.09;
  const baseFreq = soft ? 780 : 1040;

  // Filtered noise click body.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 90 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = soft ? 1200 : 1800;
  bp.Q.value = 6;
  const { node: noiseGain } = audio.envelope(ctx, {
    attack: 0.001,
    decay: 0.02,
    sustain: 0.2,
    release: dur,
    peak: soft ? 0.12 : 0.2,
  });
  audio.connectThrough(noise, bp, noiseGain, master);
  noise.start(t);
  noise.stop(t + dur + 0.04);

  // Pitched ping.
  const osc = audio.makeOscillator(ctx, {
    freq: baseFreq,
    type: 'sine',
    detune: jitter(localRand, 18),
  });
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.7, t + dur);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.001,
    decay: 0.015,
    sustain: 0.25,
    release: dur,
    peak: soft ? 0.08 : 0.14,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.04);
}

// ---------------------------------------------------------------------------
// pathDraw — brief filtered noise sweep, as if a pen drags across paper.
// ---------------------------------------------------------------------------

function synthPathDraw(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.18;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 200 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1500, t);
  bp.frequency.exponentialRampToValueAtTime(600, t + dur);
  bp.Q.value = 1.5;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.005,
    decay: 0.04,
    sustain: 0.6,
    release: dur,
    peak: 0.11,
  });
  audio.connectThrough(noise, bp, g, master);
  noise.start(t);
  noise.stop(t + dur + 0.02);
}

// ---------------------------------------------------------------------------
// wordTravel — soft wind whoosh as letters travel toward the found list.
// ---------------------------------------------------------------------------

function synthWordTravel(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.6;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 700, shaping: 1.4 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(700, t);
  bp.frequency.exponentialRampToValueAtTime(2200, t + 0.3);
  bp.frequency.exponentialRampToValueAtTime(900, t + dur);
  bp.Q.value = 1.4;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.04,
    decay: 0.1,
    sustain: 0.6,
    release: dur,
    peak: 0.15,
  });
  audio.connectThrough(noise, bp, g, master);
  noise.start(t);
  noise.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// incorrect — soft "shhh," never a buzzer.
// ---------------------------------------------------------------------------

function synthIncorrect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.35;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 400, shaping: 1.8 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1100;
  lp.Q.value = 0.7;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.05,
    decay: 0.1,
    sustain: 0.5,
    release: dur,
    peak: 0.09,
  });
  audio.connectThrough(noise, lp, g, master);
  noise.start(t);
  noise.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Themed chimes per category. Each is a distinct synthesis pattern so a
// player can recognize the theme from the sound alone.
// ---------------------------------------------------------------------------

function chimeBirdTrill(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // Two warbled sine voices trilling around a high pitch + filtered noise dust.
  const notes = [1760, 1980, 1760, 2080, 1860];
  notes.forEach((freq, i) => {
    const tt = t + i * 0.06;
    const osc = audio.makeOscillator(ctx, {
      freq,
      type: 'sine',
      detune: jitter(localRand, 25),
    });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, tt);
    g.gain.exponentialRampToValueAtTime(0.16, tt + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, tt + 0.16);
    osc.connect(g);
    g.connect(master);
    osc.start(tt);
    osc.stop(tt + 0.2);
  });
  // a wisp of high noise
  const buf = audio.noiseBuffer(ctx, { lengthMs: 220 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 3000;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.04, decay: 0.05, sustain: 0.5, release: 0.18, peak: 0.05,
  });
  audio.connectThrough(noise, hp, ng, master);
  noise.start(t);
  noise.stop(t + 0.25);
}

function chimePianoNote(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // Single warm piano-ish note: fundamental + 2 partials with quick decay.
  const fundamental = 392; // G4
  [1, 2, 3].forEach((mul, i) => {
    const osc = audio.makeOscillator(ctx, {
      freq: fundamental * mul,
      type: i === 0 ? 'triangle' : 'sine',
      detune: jitter(localRand, 8),
    });
    const g = ctx.createGain();
    const peak = 0.22 / (i + 1);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(peak * 0.35, t + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9 + i * 0.1);
    osc.connect(g);
    g.connect(master);
    osc.start(t);
    osc.stop(t + 1.1);
  });
}

function chimeSoftBell(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // Bell-ish — fundamental + minor third + octave.
  const f = 587.33; // D5
  const partials = [f, f * 1.2, f * 2.0];
  partials.forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    const peak = 0.18 / (i + 1);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.008);
    g.gain.exponentialRampToValueAtTime(peak * 0.4, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.85 + i * 0.12);
    osc.connect(g);
    g.connect(master);
    osc.start(t);
    osc.stop(t + 1.1);
  });
}

function chimeShipBell(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // Lower bell, brassier.
  const f = 349.23; // F4
  [f, f * 1.5, f * 2.0].forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, {
      freq,
      type: i === 0 ? 'triangle' : 'sine',
    });
    const g = ctx.createGain();
    const peak = 0.2 / (i + 1);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.01);
    g.gain.exponentialRampToValueAtTime(peak * 0.3, t + 0.18);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1 + i * 0.15);
    osc.connect(g);
    g.connect(master);
    osc.start(t);
    osc.stop(t + 1.4);
  });
}

function chimeProjectorClick(): void {
  // Films chime — soft rhythmic film-projector tick into a brief warm pad.
  // Two close mechanical clicks then a single warm sustained note suggest
  // the picture starting up.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // Mechanical clicks via short filtered noise bursts.
  for (let i = 0; i < 2; i += 1) {
    const tt = t + i * 0.09;
    const buf = audio.noiseBuffer(ctx, { lengthMs: 60 });
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 8;
    const { node: g } = audio.envelope(ctx, {
      attack: 0.001, decay: 0.02, sustain: 0.2, release: 0.05, peak: 0.18,
    });
    audio.connectThrough(noise, bp, g, master);
    noise.start(tt);
    noise.stop(tt + 0.08);
  }
  // Warm projection bulb hum — pad coming up after the click.
  const pad = t + 0.18;
  [261.63, 329.63, 392].forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1200;
    lp.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, pad);
    g.gain.exponentialRampToValueAtTime(0.09 / (i + 1), pad + 0.04);
    g.gain.exponentialRampToValueAtTime(0.03 / (i + 1), pad + 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, pad + 0.85 + i * 0.08);
    osc.connect(lp);
    lp.connect(g);
    g.connect(master);
    osc.start(pad);
    osc.stop(pad + 0.9);
  });
}

function chimeWoodTap(): void {
  // Animals chime — two warm hollow wood taps, like a hand on a wooden gate.
  // Lower and rounder than the projector click; an outdoor, organic feel.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const fundamentals = [330, 247]; // E4 then B3 — a quiet down-third interval
  fundamentals.forEach((freq, i) => {
    const tt = t + i * 0.13;
    // Body — pitched sine with a quick down-glide for a tapped-wood feel.
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    osc.frequency.exponentialRampToValueAtTime(freq * 0.85, tt + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, tt);
    g.gain.exponentialRampToValueAtTime(0.22, tt + 0.005);
    g.gain.exponentialRampToValueAtTime(0.06, tt + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, tt + 0.5);
    osc.connect(g);
    g.connect(master);
    osc.start(tt);
    osc.stop(tt + 0.6);
    // A faint mid-band noise transient for the "tap" attack.
    const buf = audio.noiseBuffer(ctx, { lengthMs: 60 });
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400;
    bp.Q.value = 5;
    const { node: ng } = audio.envelope(ctx, {
      attack: 0.001, decay: 0.01, sustain: 0.2, release: 0.04, peak: 0.12,
    });
    audio.connectThrough(noise, bp, ng, master);
    noise.start(tt);
    noise.stop(tt + 0.07);
  });
}

function synthChime(kind: ChimeKind): void {
  switch (kind) {
    case 'bird-trill': return chimeBirdTrill();
    case 'piano-note': return chimePianoNote();
    case 'soft-bell': return chimeSoftBell();
    case 'ship-bell': return chimeShipBell();
    case 'projector-click': return chimeProjectorClick();
    case 'wood-tap': return chimeWoodTap();
  }
}

// ---------------------------------------------------------------------------
// Card-enter — soft paper rustle.
// ---------------------------------------------------------------------------

function synthCardEnter(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 350, shaping: 1.7 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1200;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(2200, t);
  bp.frequency.exponentialRampToValueAtTime(900, t + 0.3);
  bp.Q.value = 1.0;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.03, decay: 0.08, sustain: 0.55, release: 0.3, peak: 0.12,
  });
  audio.connectThrough(noise, hp, bp, g, master);
  noise.start(t);
  noise.stop(t + 0.4);
}

// ---------------------------------------------------------------------------
// Complete — layered three-sine chord, theme-tuned via root frequency.
// ---------------------------------------------------------------------------

function synthComplete(root = 261.63): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  // root + major third + perfect fifth + an octave shimmer
  const intervals = [1, 1.25, 1.5, 2.0];
  intervals.forEach((mul, i) => {
    const tt = t + i * 0.08;
    const osc = audio.makeOscillator(ctx, {
      freq: root * mul,
      type: 'sine',
      detune: jitter(localRand, 6),
    });
    const g = ctx.createGain();
    const peak = 0.16 - i * 0.03;
    g.gain.setValueAtTime(0.0001, tt);
    g.gain.exponentialRampToValueAtTime(peak, tt + 0.04);
    g.gain.exponentialRampToValueAtTime(peak * 0.5, tt + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, tt + 1.6 + i * 0.1);
    osc.connect(g);
    g.connect(master);
    osc.start(tt);
    osc.stop(tt + 2.0);
  });
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class ThemedSound {
  /** Lazy unlock — call on first user gesture. */
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: ThemedSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'letterTap': return synthLetterTap(false);
        case 'letterSelect': return synthLetterTap(true);
        case 'pathDraw': return synthPathDraw();
        case 'wordTravel': return synthWordTravel();
        case 'incorrectSoft': return synthIncorrect();
        case 'cardEnter': return synthCardEnter();
        case 'complete': return synthComplete();
      }
    });
  }

  /** Themed chime for the active puzzle. */
  playChime(kind: ChimeKind): void {
    audio.playIfNotMuted(`chime-${kind}`, () => {
      audio.unlockAudio();
      synthChime(kind);
    });
  }
}

export const sound = new ThemedSound();
